import React, { useEffect, useMemo, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import PlusIcon from "../../svg/PlusIcon";
import CustomTooltip from "../common/CustomTooltip";
import RecordTable, { RecordColumn } from "../common/RecordTable";
import {
  enPrecisionTestKind,
  getProviderLabel,
  getPanelTestKinds,
  getPanelTestKindsLabel,
} from "../../store/helper";
import LabSvg from "../../../../common/svgs/LabSvg";
import {
  isPanelInactiveAtLab,
  resolvePanelLabStatus,
  getStatusBadgeStyle,
} from "../../utils/labPanelStatus";
import { formatDisplayDate } from "../../../../common/utils/date";
import { useSelector, useDispatch } from "react-redux";
import { getProviderLabPanels } from "../../../../common/network/Api";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../../store/service-logic-builder-slice";

// Short labels used for the provider tab, e.g. "Quest default"
const PROVIDER_SHORT_MAP: Record<string, string> = {
  quest: "Quest",
  labcorp: "LabCorp",
};

// Provider whose pre-defined panels populate the default tab
export const DEFAULT_PROVIDER = "quest";

// All providers whose read-only catalogs we know how to fetch. Used to resolve
// a provider panel by id when deep-linking to the detail screen.
export const KNOWN_PROVIDERS = Object.keys(PROVIDER_SHORT_MAP);

// Provider panels carry a panel-level `price`; user panels sum their items'
// prices. Prefer the panel price when present, otherwise sum the items.
// Rounded to 2 decimals to avoid floating-point artifacts (e.g. 467.16999…).
export const getPanelCost = (panel: any): number => {
  // Cost lives at the panel level: `price` on catalog objects, `total` on the
  // saved lab-panel entity. Sum item prices only when neither is set.
  let raw: number;
  if (typeof panel.price === "number" && panel.price > 0) {
    raw = panel.price;
  } else if (typeof panel.total === "number" && panel.total > 0) {
    raw = panel.total;
  } else {
    raw =
      panel.items?.reduce(
        (sum: number, item: any) => sum + (item.price || 0),
        0
      ) || 0;
  }
  return Math.round(raw * 100) / 100;
};

// Provider panels have no panel-level `kind`; derive it from their items so the
// table can render a test type the same way it does for user panels.
export const normalizeProviderPanel = (panel: any): any => ({
  ...panel,
  kind:
    panel.kind ??
    (Array.isArray(panel.items) && panel.items.length > 0
      ? panel.items[0].kind
      : null),
});

const getTestKindLabel = (kind: number | null): string => {
  if (kind === null || kind === undefined) return "";
  return enPrecisionTestKind[kind] || "";
};

interface SavedPanelsScreenProps {
  panels: any[];
  setOpenModal: (open: boolean) => void;
  onPanelClick: (panel: any, isProvider?: boolean) => void;
}

const SavedPanelsScreen: React.FC<SavedPanelsScreenProps> = ({
  panels,
  setOpenModal,
  onPanelClick,
}) => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  // Active tab is persisted in Redux so returning from a panel's detail screen
  // (back button) lands on the same tab the user was on.
  const activeTab = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.activeLabTab
  );
  const setActiveTab = (tab: "custom" | "provider") =>
    dispatch(serviceLogicBuilderActions.setActiveLabTab(tab));
  // Every filter lives in a column header now (`RecordTable`), matching the
  // History and Logic Builder listings. There is no separate search field: the
  // three things it searched — name, provider, test type — each filter from
  // their own header, and the Name column's menu carries its own search box.
  const [clearFiltersSlot, setClearFiltersSlot] = useState<HTMLElement | null>(
    null
  );

  // Provider panels are cached in Redux so the detail screen can resolve a
  // panel by id without re-fetching after the listing has loaded them.
  // `null` means "not loaded yet" — which is also our loading signal.
  const providerPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.providerLabPanels[DEFAULT_PROVIDER] ?? null
  );
  const providerLoading = activeTab === "provider" && providerPanels === null;

  // Lazily load the provider's pre-defined panels the first time the
  // "{Provider} default" tab is opened.
  useEffect(() => {
    if (activeTab !== "provider" || providerPanels !== null) return;

    let cancelled = false;
    getProviderLabPanels(DEFAULT_PROVIDER)
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response)
          ? response.map(normalizeProviderPanel)
          : [];
        dispatch(
          serviceLogicBuilderActions.setProviderLabPanels({
            provider: DEFAULT_PROVIDER,
            panels: list,
          })
        );
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(
          serviceLogicBuilderActions.setProviderLabPanels({
            provider: DEFAULT_PROVIDER,
            panels: [],
          })
        );
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, providerPanels, dispatch]);

  const isProviderTab = activeTab === "provider";
  // Panels the lab has retired can no longer be ordered, so they drop out of
  // the listing entirely rather than sitting there unusable. Provider catalog
  // panels carry no lab status, so the predicate never removes any of those.
  // Custom panels list A–Z by name so a saved panel is easy to find; the
  // provider catalog keeps the lab's own order.
  const sourcePanels = useMemo(() => {
    const all = isProviderTab ? providerPanels ?? [] : panels;
    const active = all.filter((panel: any) => !isPanelInactiveAtLab(panel));
    if (isProviderTab) return active;
    return [...active].sort((a: any, b: any) =>
      String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [isProviderTab, providerPanels, panels]);

  const columns = useMemo<RecordColumn<any>[]>(
    () => [
      {
        key: "name",
        label: "Panel name",
        flex: 2.2,
        searchable: true,
        value: (panel) => panel.name,
      },
      {
        key: "provider",
        label: "Provider",
        flex: 1.4,
        filter: "multi",
        optionKey: (panel) => String(panel.provider ?? ""),
        value: (panel) => getProviderLabel(panel.provider),
      },
      {
        key: "testType",
        label: "Test type",
        flex: 1.6,
        filter: "multi",
        // A panel can hold items of several kinds, so it matches any of the
        // selected ones — the same multi-valued case the Prescription tab's
        // Services column uses.
        optionsOf: (panel) =>
          getPanelTestKinds(panel).map((kind) => ({
            key: String(kind),
            label: getTestKindLabel(kind),
          })),
        value: (panel) => getPanelTestKindsLabel(panel),
      },
      // Lab status only exists on our own panels — the provider catalog
      // carries no `lab_panel_status`, so the column is dropped there rather
      // than rendering a row of blanks.
      ...(isProviderTab
        ? []
        : [
            {
              key: "status",
              label: "Status",
              flex: 1.3,
              filter: "multi" as const,
              value: (panel: any) => resolvePanelLabStatus(panel)?.label,
              render: (panel: any) => {
                const labStatus = resolvePanelLabStatus(panel);
                if (!labStatus) return null;
                return (
                  <div style={getStatusBadgeStyle(labStatus.tone, theme)}>
                    {labStatus.label}
                  </div>
                );
              },
            },
          ]),
      {
        key: "biomarkers",
        label: "Biomarkers",
        flex: 1,
        cellStyle: "node",
        value: (panel) => String(panel.items?.length || 0),
        render: (panel) => (
          <div style={styles.biomarkerCell}>
            <LabSvg
              fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT}
              width={12}
              height={13}
            />
            <CustomTooltip
              type="medium"
              label="Biomarkers"
              message={
                panel.items?.length > 0 ? (
                  <span>
                    {panel.items
                      .map(
                        (item: any) => `&bull; ${item.name || item.biomarkerid}`
                      )
                      .join("<br/>")}
                  </span>
                ) : undefined
              }
              customHeight="auto"
            >
              <span>{panel.items?.length || 0}</span>
            </CustomTooltip>
          </div>
        ),
      },
      {
        key: "cost",
        label: "Total cost",
        flex: 1,
        value: (panel) => `$${getPanelCost(panel)}`,
      },
      {
        key: "date",
        label: "Date",
        // Wider than a date needs: the filter shows the applied range here.
        flex: 1.8,
        filter: "date",
        date: (panel) => panel.dateUpdated || panel.dateCreated,
        value: (panel) =>
          formatDisplayDate(panel.dateUpdated || panel.dateCreated),
      },
    ],
    [theme, isProviderTab]
  );

  return (
    <div style={styles.container}>
      {/* One toolbar line: tabs, then Clear filters, then New Panel. */}
      <div style={styles.filtersRow}>
        {/* Provider catalog first: it is the usual place to pick from. */}
        <div style={styles.tabs}>
          <button
            type="button"
            style={styles.tab(theme, isProviderTab)}
            onClick={() => setActiveTab("provider")}
          >
            {`${
              PROVIDER_SHORT_MAP[DEFAULT_PROVIDER] || DEFAULT_PROVIDER
            } default`}
          </button>
          <button
            type="button"
            style={styles.tab(theme, !isProviderTab)}
            onClick={() => setActiveTab("custom")}
          >
            Custom
          </button>
        </div>
        <div style={styles.toolbarSpacer} />
        {/* The table portals its Clear-filters pill here, so applying a
            column filter does not push the rows down. */}
        <div ref={setClearFiltersSlot} style={styles.clearFiltersSlot} />
        <PrimaryButton
          label="New Panel"
          icon={<PlusIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />}
          onClick={() => setOpenModal(true)}
          // Sharing the row, the button must not be squeezed into wrapping
          // its label.
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        />
      </div>

      <RecordTable<any>
        columns={columns}
        rows={sourcePanels}
        rowKey={(panel) => panel.id}
        loading={isProviderTab && providerLoading}
        onRowClick={(panel) => onPanelClick(panel, isProviderTab)}
        clearFiltersSlot={clearFiltersSlot}
        emptyMessage="No panels found matching your search"
      />
    </div>
  );
};

export default SavedPanelsScreen;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flex: 1,
    minHeight: 0,
  },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexShrink: 0,
  },
  toolbarSpacer: {
    flex: 1,
  },
  clearFiltersSlot: {
    display: "flex",
    alignItems: "center",
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  tab: (theme: Theme, active: boolean) => ({
    background: "none",
    border: "none",
    padding: "4px 0",
    cursor: "pointer",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: active
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    borderBottom: active
      ? `2px solid ${theme.colors_ds.PRIMARY_TEXT_MAIN}`
      : "2px solid transparent",
  }),
  biomarkerCell: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
};
